import React, { useContext, useMemo, useState, useEffect } from "react";
import { DashboardContext } from "states/DashboardContext";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Filter, Rows, Columns, Download } from "lucide-react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highmaps";
import Drilldown from "highcharts/modules/drilldown";
import DataModule from "highcharts/modules/data";
import Exporting from "highcharts/modules/exporting";
import Heatmap from "highcharts/modules/heatmap";
import More from "highcharts/highcharts-more";
import geoService from "util/geoserver";
import L from "leaflet";
import { ContextMenu } from "handsontable/plugins";

// Initialize modules
Drilldown(Highcharts);
DataModule(Highcharts);
Exporting(Highcharts);
Heatmap(Highcharts);
More(Highcharts);

const chartTypes = [
  "Table",
  "Table Heatmap",
  "Grouped Column Chart",
  "Stacked Column Chart",
  "Line Chart",
  "Dot Chart",
  "Map Chart",
];

function getMapFilterParams(location, cql_filter) {
  return L.Util.extend({
    service: "WFS",
    request: "GetFeature",
    typeName: "cite:" + location,
    cql_filter,
    outputFormat: "application/json",
  });
}

const getValue = (item, key) => {
  if (key === "indicator_name") {
    return `${item.indicator_name} (${item.subgroup_name})`;
  }
  return item[key];
};

const getKey = (item, keys) => keys.map((k) => getValue(item, k)).join(" | ");

const downloadCSV = (headers, rows) => {
  const csvRows = [
    headers.join(","),
    ...rows.map((row) => row.map((v) => `"${v}"`).join(",")),
  ];
  const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "pivot-table.csv";
  a.click();
  URL.revokeObjectURL(url);
};

const CensusPivotTable = ({ data }) => {
  const {
    rowItemState,
    columnItemState,
    filterItemState,
    chartTypeState,
    censusIsDefaultDataState,
  } = useContext(DashboardContext);

  const [rowItem, setRowItem] = rowItemState;
  const [columnItem, setColumnItem] = columnItemState;
  const [filterItem, setFilterItem] = filterItemState;
  const [chartType, setChartType] = chartTypeState;
  const [filterValues, setFilterValues] = useState({});
  const [censusIsDefaultData, setCensusIsDefaultData] =
    censusIsDefaultDataState;
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "asc",
  });

  const rowHeaderLabel = rowItem.map((r) => r.label).join(" / ");
  const rowKeys = rowItem.map((r) => r.id);
  const colKeys = columnItem.map((c) => c.id);
  const filterKeys = filterItem.map((f) => f.id);
  const [mapColor, setMapColor] = useState("#1c6d4a");

  const availableFilterOptions = useMemo(() => {
    const options = {};
    filterKeys.forEach((key) => {
      options[key] = [...new Set(data.map((d) => getValue(d, key)))];
    });
    return options;
  }, [data, filterKeys]);

  useEffect(() => {
    const missing = filterKeys.filter((fk) => !(fk in filterValues));
    if (missing.length === 0) return;

    const defaults = {};
    missing.forEach((fk) => {
      defaults[fk] = availableFilterOptions[fk]?.[0] ?? "";
    });

    setFilterValues((prev) => ({ ...prev, ...defaults }));
  }, [filterKeys, filterValues, availableFilterOptions]);

  const filteredData = useMemo(() => {
    return data.filter((item) =>
      filterKeys.every((fk) => {
        const selected = filterValues[fk];
        return !selected || getValue(item, fk) === selected;
      })
    );
  }, [data, filterKeys, filterValues]);

  const enableMapChart = useMemo(() => {
    if (filteredData.length === 0) return false;

    const times = new Set(filteredData.map((d) => getValue(d, "time_name")));

    const indicators = new Set(
      filteredData.map((d) => getValue(d, "indicator_name"))
    );

    const levels = new Set(filteredData.map((d) => getValue(d, "area_level")));

    const areaKeys = new Set(filteredData.map((d) => getValue(d, "areakey")));

    return (
      times.size === 1 &&
      indicators.size === 1 &&
      levels.size === 1 &&
      areaKeys.size > 1
    );
  }, [filteredData]);

  const availableChartTypes = useMemo(() => {
    const base = [
      "Table",
      "Table Heatmap",
      "Grouped Column Chart",
      "Stacked Column Chart",
      "Line Chart",
      "Dot Chart",
    ];
    if (enableMapChart) base.push("Map Chart");
    return base;
  }, [enableMapChart]);

  const effectiveChartType = useMemo(() => {
    return availableChartTypes.includes(chartType)
      ? chartType
      : availableChartTypes[0];
  }, [availableChartTypes, chartType]);

  const chartIsStillValid = availableChartTypes.includes(chartType);

  const { rowHeaders, columnHeaders, pivotData, multiColMap } = useMemo(() => {
    if (rowKeys.length === 0 || colKeys.length === 0) {
      return {
        rowHeaders: [],
        columnHeaders: [],
        pivotData: {},
        multiColMap: {},
      };
    }

    const rowSet = new Set();
    const colSet = new Set();
    const pivot = {};
    const multiCol = {};

    filteredData.forEach((item) => {
      const rowKey = getKey(item, rowKeys);
      const colKey = getKey(item, colKeys);

      rowSet.add(rowKey);
      colSet.add(colKey);

      pivot[`${rowKey}|${colKey}`] = item.data_value;

      if (!multiCol[colKey]) {
        multiCol[colKey] = colKeys.map((k) => getValue(item, k));
      }
    });

    return {
      rowHeaders: Array.from(rowSet).sort(),
      columnHeaders: Array.from(colSet).sort(),
      pivotData: pivot,
      multiColMap: multiCol,
    };
  }, [filteredData, rowKeys, colKeys]);

  const [rowWarning, setRowWarning] = useState(false);
  const [colWarning, setColWarning] = useState(false);

  const chosenLevel = useMemo(() => {
    const lv = new Set(filteredData.map((d) => d.area_level));
    return lv.size === 1 ? [...lv][0] : null; // e.g. "LVL3" or "LVL5"
  }, [filteredData]);

  const areaCodes = useMemo(() => {
    if (!chosenLevel) return [];
    return [...new Set(filteredData.map((d) => String(d.area_code)))].sort();
  }, [filteredData, chosenLevel]);

  const areaCodesKey = useMemo(
    () => areaCodes.sort((a, b) => a - b).join(","),
    [areaCodes]
  );

  const fieldMap = {
    LVL3: "Region_Cod",
    LVL5: "Council_ID",
    LVL7: "ward_code",
  };

  const [rawFeatures, setRawFeatures] = useState([]);

  useEffect(() => {
    if (chartType !== "Map Chart" || !chosenLevel || !areaCodesKey) {
      setRawFeatures([]);
      return;
    }

    const layerMap = {
      LVL3: "CENSUS-TZ-REGIONS",
      LVL5: "CENSUS-TZ-COUNCILS",
      LVL7: "CENSUS-TZ-WARDS",
    };
    const layer = layerMap[chosenLevel];
    const field = fieldMap[chosenLevel];

    // wrap strings in quotes for CQL
    const inList = areaCodes.map((c) => `'${c}'`).join(",");
    const params = getMapFilterParams(layer, `${field} IN (${inList})`);

    geoService(params)
      .then(({ features }) => {
        setRawFeatures(features);
      })
      .catch(console.error);
  }, [chartType, chosenLevel, areaCodesKey]);

  // now, every render we take those raw shapes and “stamp on” the latest value
  const mapData = useMemo(() => {
    const field = fieldMap[chosenLevel];
    return rawFeatures.map((f) => {
      const code = String(f.properties[field]);
      const match = filteredData.find((d) => String(d.area_code) === code);
      return {
        ...f,
        id: code,
        value: Number(match?.data_value) || 0,
      };
    });
  }, [rawFeatures, filteredData, chosenLevel]);

  const totalValue = useMemo(
    () => mapData.reduce((sum, f) => sum + (f.value || 0), 0),
    [mapData]
  );

  const mapChartOptions = useMemo(() => {
    if (chartType !== "Map Chart" || !filteredData.length || !mapData.length) {
      return {};
    }

    const fieldMap = {
      LVL3: "Region_Cod",
      LVL5: "Council_ID",
      LVL7: "ward_code",
    };
    const field = fieldMap[chosenLevel];

    //compute  data-range
    const vals = mapData.map((f) => f.value);
    const min = Math.min(...vals);
    const max = Math.max(...vals);

    // wrap  features in a FeatureCollection
    const geojson = { type: "FeatureCollection", features: mapData };

    const dataForMap = mapData.map((f) => ({
      id: f.id,
      value: f.value,
    }));

    return {
      chart: { type: "map", height: 500, spacingRight: 150 },
      legend: {
        enabled: true,
        layout: "vertical",
        align: "right",
        verticalAlign: "middle",
        itemStyle: { fontWeight: "normal" },
      },
      title: { text: getValue(filteredData[0], "indicator_name") },
      exporting: {
        enabled: true,
        // buttons: {
        //   contextButton: {
        //     enabled: false,
        //   },
        // },
      },

      colorAxis: {
        min,
        max,
        minColor: "#F2FFE9",
        maxColor: mapColor,
      },

      mapNavigation: { enabled: true },

      series: [
        {
          allAreas: true,
          nullColor: "#EEE",
          showInLegend: false,
          enableMouseTracking: false,
        },

        {
          type: "map",
          mapData: geojson,
          data: dataForMap,

          joinBy: [field, "id"],

          name: "Value",
          states: { hover: { color: "lightblue" } },

          dataLabels: {
            color: "white",
            style: { fontWeight: "bold", fontSize: "15px" },
            enabled: true,
            formatter() {
              const nameMap = {
                LVL3: "Region_Nam",
                LVL5: "Council_N",
                LVL7: "ward_name",
              };
              const nm = this.point.properties?.[nameMap[chosenLevel]] || "";
              const val =
                this.point.value != null
                  ? Highcharts.numberFormat(this.point.value, 0)
                  : "-";
              return `<div style="text-align:center">${nm}<br/>${val}</div>`;
            },
          },
          tooltip: {
            formatter() {
              const nameMap = {
                LVL3: "Region_Nam",
                LVL5: "Council_N",
                LVL7: "ward_name",
              };
              const nm = this.point.properties?.[nameMap[chosenLevel]] || "";
              return `<b>${nm}</b><br/>Value: ${Highcharts.numberFormat(
                this.point.value,
                0
              )}`;
            },
          },
        },
      ],
    };
  }, [chartType, mapData, chosenLevel, filteredData]);

  const onDragEnd = (result) => {
    if (
      !result.destination ||
      result.source.droppableId === result.destination.droppableId
    )
      return;

    const sourceId = result.source.droppableId;
    const destId = result.destination.droppableId;

    const getStateSetter = (id) => {
      switch (id) {
        case "filters":
          return [filterItem, setFilterItem];
        case "rows":
          return [rowItem, setRowItem];
        case "columns":
          return [columnItem, setColumnItem];
        default:
          return [[], () => {}];
      }
    };

    const [sourceList, setSourceList] = getStateSetter(sourceId);
    const [destList, setDestList] = getStateSetter(destId);

    const newSource = [...sourceList];
    const newDest = [...destList];

    const [moved] = newSource.splice(result.source.index, 1);
    newDest.splice(result.destination.index, 0, moved);

    setSourceList(newSource);
    setDestList(newDest);

    if (sourceId === "rows" && newSource.length === 0) setRowWarning(true);
    if (sourceId === "columns" && newSource.length === 0) setColWarning(true);
    if (destId === "rows") setRowWarning(false);
    if (destId === "columns") setColWarning(false);
  };

  const sortedRowHeaders = useMemo(() => {
    if (!sortConfig.key) return rowHeaders;

    return [...rowHeaders].sort((a, b) => {
      let result;

      if (sortConfig.key === "__row__") {
        // sort by row label itself
        result = a.localeCompare(b);
      } else {
        const valA = pivotData[`${a}|${sortConfig.key}`];
        const valB = pivotData[`${b}|${sortConfig.key}`];

        const aNum = Number(valA);
        const bNum = Number(valB);

        result =
          !isNaN(aNum) && !isNaN(bNum)
            ? aNum - bNum
            : String(valA || "").localeCompare(String(valB || ""));
      }

      return sortConfig.direction === "asc" ? result : -result;
    });
  }, [rowHeaders, sortConfig, pivotData]);

  const renderDraggableItem = (item, IconComponent) => (
    <Draggable key={item.id} draggableId={item.id} index={item.index}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          title="Drag to move or reorder"
          className="flex items-center gap-1 text-xs bg-white px-3 py-1 rounded border shadow cursor-pointer transition-transform hover:scale-105 hover:shadow-md active:scale-95"
        >
          <IconComponent className="w-3 h-3 text-gray-500" />
          <span>{item.label}</span>
        </div>
      )}
    </Draggable>
  );

  const handleDownload = () => {
    const headers = [rowKeys.join(" / "), ...columnHeaders];
    const rows = rowHeaders.map((rowKey) => {
      return [
        rowKey,
        ...columnHeaders.map(
          (colKey) => pivotData[`${rowKey}|${colKey}`] ?? ""
        ),
      ];
    });
    downloadCSV(headers, rows);
  };

  const lineChartOptions = useMemo(() => {
    if (!rowHeaders.length || !columnHeaders.length) return {};

    const series = columnHeaders.map((colKey) => ({
      name: colKey,
      data: rowHeaders.map((rowKey) => {
        const val = pivotData[`${rowKey}|${colKey}`];
        return val !== undefined ? Number(val) : null;
      }),
    }));

    return {
      title: { text: "" },
      xAxis: {
        categories: rowHeaders,
        title: { text: rowKeys.join(" / ") },
      },
      yAxis: {
        title: { text: "Values" },
      },
      series,
      chart: {
        type: "line",
      },
    };
  }, [rowHeaders, columnHeaders, pivotData]);

  const groupedColumnChartOptions = useMemo(() => {
    if (!rowHeaders.length || !columnHeaders.length) return null;

    return {
      chart: {
        type: "column",
      },
      title: {
        text: "",
      },
      xAxis: {
        categories: rowHeaders,
        title: { text: rowKeys.join(" / ") },
      },
      yAxis: {
        title: { text: "Value" },
      },
      series: columnHeaders.map((colKey) => ({
        name: colKey,
        data: rowHeaders.map(
          (rowKey) => Number(pivotData[`${rowKey}|${colKey}`]) || 0
        ),
      })),
    };
  }, [rowHeaders, columnHeaders, pivotData]);

  const getHeatmapColor = (value) => {
    if (value === undefined || value === null) return "transparent";
    const percent = (value - minValue) / (maxValue - minValue || 1);
    const blue = Math.floor(255 - percent * 155); // 255 to 100
    return `rgb(${blue}, ${blue}, 255)`; // light blue to medium blue
  };

  const allValues = Object.values(pivotData).filter(
    (v) => typeof v === "number"
  );
  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);

  const rowCount = rowHeaders.length;
  const baseHeightPerRow = 40;
  const minHeight = 300;
  const maxHeight = 1000;

  const dynamicHeight = Math.min(
    Math.max(rowCount * baseHeightPerRow, minHeight),
    maxHeight
  );

  //chart options for table heatmap
  const tableHeatmapOptions = {
    chart: {
      type: "heatmap",
      plotBorderWidth: 1,
      height: dynamicHeight,
    },
    title: { text: "" },
    xAxis: {
      categories: columnHeaders.map((colKey) => colKey.replace(/\|/g, " / ")),
      title: { text: "Columns" },
    },
    yAxis: {
      categories: rowHeaders,
      title: { text: null },
      reversed: true,
    },
    colorAxis: {
      min: 0,
      minColor: "#FFFFFF",
      maxColor: Highcharts.getOptions().colors[0],
    },
    legend: {
      align: "right",
      layout: "vertical",
      margin: 0,
      verticalAlign: "top",
      y: 25,
      symbolHeight: 280,
    },
    tooltip: {
      formatter: function () {
        const row = this.series.yAxis.categories[this.point.y];
        const col = this.series.xAxis.categories[this.point.x]?.split("__")[0];
        const value = this.point.value;
        return `<b>${row}</b><br/>${col}: <b>${value}</b>`;
      },
    },
    series: [
      {
        name: "Values",
        borderWidth: 1,
        data: rowHeaders.flatMap((rowKey, rowIndex) =>
          columnHeaders.map((colKey, colIndex) => {
            const value = pivotData[`${rowKey}|${colKey}`];
            return [colIndex, rowIndex, value != null ? value : null];
          })
        ),
        dataLabels: {
          enabled: true,
          color: "#000000",
        },
      },
    ],
  };

  //chart options for stacked column chart
  const stackedChartOptions = useMemo(() => {
    if (chartType !== "Stacked Column Chart") return null;

    const categories = columnHeaders; // X-axis labels from column headers
    const seriesData = rowHeaders.map((rowKey) => ({
      name: rowKey,
      data: columnHeaders.map((colKey) => {
        const value = pivotData[`${rowKey}|${colKey}`];
        return value !== undefined ? Number(value) : 0;
      }),
    }));

    return {
      chart: {
        type: "column",
      },
      title: {
        text: "",
      },
      xAxis: {
        categories,
        title: { text: columnItem.map((c) => c.label).join(" / ") },
      },
      yAxis: {
        min: 0,
        title: {
          text: "Values",
        },
        stackLabels: {
          enabled: false,
          style: {
            // fontWeight: "bold",
            color: Highcharts?.defaultOptions?.title?.style?.color || "black",
          },
        },
      },
      legend: {
        align: "right",
        x: -30,
        verticalAlign: "top",
        y: 25,
        floating: true,
        backgroundColor:
          Highcharts.defaultOptions.legend.backgroundColor || "white",
        borderColor: "#CCC",
        borderWidth: 1,
        shadow: false,
      },
      tooltip: {
        headerFormat: "<b>{point.x}</b><br/>",
        pointFormat: "{series.name}: {point.y}<br/>Total: {point.stackTotal}",
      },
      plotOptions: {
        column: {
          stacking: "normal",
          dataLabels: {
            enabled: true,
          },
        },
      },
      series: seriesData,
    };
  }, [chartType, rowHeaders, columnHeaders, pivotData, columnItem]);

  //Implementation of Dot Chart
  const dotPlotSeries = useMemo(() => {
    if (!rowHeaders.length || !columnHeaders.length) return [];

    return rowHeaders.map((rowKey) => ({
      name: rowKey,
      data: columnHeaders
        .map((colKey, i) => {
          const val = pivotData[`${rowKey}|${colKey}`];
          return {
            x: i,
            y: val != null ? parseFloat(val) : null,
            name: colKey,
          };
        })
        .filter((d) => d.y !== null),
    }));
  }, [pivotData, rowHeaders, columnHeaders]);

  const dotPlotOptions = {
    chart: {
      type: "scatter",
      zoomType: "xy",
    },
    title: {
      text: "",
    },
    xAxis: {
      categories: columnHeaders,
      title: {
        text: colKeys.join(" / ") || "Columns",
      },
    },
    yAxis: {
      title: {
        text: "Value",
      },
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 5,
          symbol: "circle",
        },
        tooltip: {
          pointFormat: "<b>{point.name}</b><br/>Value: {point.y}",
        },
      },
    },
    series: dotPlotSeries,
  };

  return (
    <div className="p-4 space-y-4">
      {!censusIsDefaultData && data?.length === 0 && (
        <div>
          <h1 className="text-center text-gray-700 ">No Data</h1>
        </div>
      )}
      {data.length > 0 && (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="flex gap-4">
            <div className="w-[140px]" />

            {/* Filter + Columns + Chart Type + Download */}
            <div className="flex-1 space-y-2">
              <div className="flex flex-wrap items-end justify-between gap-4">
                {/* Filters */}
                <div className="flex-1 min-w-[250px]">
                  <label className="block font-semibold mb-1">Filters</label>
                  <Droppable droppableId="filters" direction="horizontal">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="flex flex-wrap gap-4 p-2 border-2 border-blue-300 rounded-lg bg-blue-50"
                      >
                        {filterItem.map((item, index) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-2"
                          >
                            <Draggable draggableId={item.id} index={index}>
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className="flex items-center gap-1 text-xs bg-white px-3 py-1 rounded border shadow cursor-pointer"
                                >
                                  <Filter className="w-3 h-3 text-gray-500" />
                                  <span>{item.label}</span>
                                </div>
                              )}
                            </Draggable>
                            <select
                              className="border p-1 rounded text-sm"
                              value={
                                filterValues[item.id] ||
                                availableFilterOptions[item.id]?.[0] ||
                                ""
                              }
                              onChange={(e) =>
                                setFilterValues((prev) => ({
                                  ...prev,
                                  [item.id]: e.target.value,
                                }))
                              }
                            >
                              {availableFilterOptions[item.id]?.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
                {/* Rows */}
                <div>
                  <label className="block font-semibold mb-1">Rows</label>
                  <Droppable droppableId="rows" direction="vertical">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="flex  gap-2 p-2 border-2 border-purple-300 rounded-lg bg-purple-50 min-w-[200px]"
                      >
                        {rowItem.map((item, index) =>
                          renderDraggableItem({ ...item, index }, Rows)
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>

                {/* Columns */}
                <div className="min-w-[200px]">
                  <label className="block font-semibold mb-1">Columns</label>
                  <Droppable droppableId="columns" direction="horizontal">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="flex gap-2 p-2 border-2 border-green-300 rounded-lg bg-green-50"
                      >
                        {columnItem.map((item, index) =>
                          renderDraggableItem({ ...item, index }, Columns)
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>

                {/* Chart Type */}
                <div className="min-w-[200px]">
                  <label className="block font-semibold mb-1">Chart Type</label>
                  <select
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value)}
                    className="w-full border p-2 rounded text-sm"
                  >
                    {availableChartTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Download */}
                <div className="min-w-fit">
                  <label className="block font-semibold mb-1 invisible">
                    Download
                  </label>
                  <button
                    onClick={handleDownload}
                    className="flex items-center gap-1 text-sm text-white bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded shadow"
                  >
                    <Download className="w-4 h-4" />
                    Download CSV
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Rows + Pivot Table */}
          <div className="flex gap-4">
            {/* Render Table (chartType check in future) */}
            <div className="w-[90vw] overflow-x-auto border border-gray-300 rounded max-h-[80vh]">
              {rowWarning && (
                <p className="text-red-600 text-sm p-2">
                  At least one row is required.
                </p>
              )}
              {colWarning && (
                <p className="text-red-600 text-sm p-2">
                  At least one column is required.
                </p>
              )}

              {effectiveChartType === "Table" &&
                rowKeys.length > 0 &&
                colKeys.length > 0 && (
                  <table className="min-w-full table-auto text-sm border-collapse">
                    <thead className="sticky top-0 z-10 bg-white">
                      {colKeys.map((colDim, level) => (
                        <tr key={`col-header-${level}`}>
                          {level === 0 && columnHeaders?.length > 0 && (
                            <th
                              title="Click to sort"
                              className="bg-gray-100 border border-gray-300 cursor-pointer hover:bg-gray-200"
                              rowSpan={colKeys.length}
                              colSpan={1}
                              onClick={() => {
                                setSortConfig((prev) => ({
                                  key: "__row__",
                                  direction:
                                    prev.key === "__row__" &&
                                    prev.direction === "asc"
                                      ? "desc"
                                      : "asc",
                                }));
                              }}
                            >
                              {rowHeaderLabel}
                              <span className="ml-1 text-gray-500">
                                {sortConfig.key === "__row__"
                                  ? sortConfig.direction === "asc"
                                    ? "▲"
                                    : "▼"
                                  : "⇅"}
                              </span>
                            </th>
                          )}

                          {columnHeaders.map((colKey) => (
                            <th
                              title="Click to sort"
                              key={`${colKey}-${level}`}
                              className="border border-gray-300 bg-gray-100 whitespace-nowrap px-2 py-1 text-center cursor-pointer"
                              onClick={() => {
                                setSortConfig((prev) => ({
                                  key: colKey,
                                  direction:
                                    prev.key === colKey &&
                                    prev.direction === "asc"
                                      ? "desc"
                                      : "asc",
                                }));
                              }}
                            >
                              {multiColMap[colKey]?.[level] || ""}
                              {level === 0 && (
                                <span className="ml-1 text-gray-500">
                                  {sortConfig.key === colKey
                                    ? sortConfig.direction === "asc"
                                      ? "▲"
                                      : "▼"
                                    : "⇅"}
                                </span>
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {sortedRowHeaders.map((rowKey) => (
                        <tr key={rowKey}>
                          <td className="p-2 border border-gray-300 font-medium bg-gray-50">
                            {rowKey}
                          </td>
                          {columnHeaders.map((colKey) => {
                            const value = pivotData[`${rowKey}|${colKey}`];
                            return (
                              <td
                                key={colKey}
                                className="p-2 border border-gray-300 text-center"
                              >
                                {value != null ? value : "-"}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

              {effectiveChartType === "Line Chart" &&
                rowHeaders.length > 0 &&
                columnHeaders.length > 0 && (
                  <div className="w-full overflow-x-auto border border-gray-300 rounded p-4 bg-white">
                    <HighchartsReact
                      highcharts={Highcharts}
                      options={lineChartOptions}
                    />
                  </div>
                )}

              {effectiveChartType === "Grouped Column Chart" &&
                groupedColumnChartOptions && (
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={groupedColumnChartOptions}
                  />
                )}

              {effectiveChartType === "Table Heatmap" &&
                rowHeaders.length > 0 &&
                columnHeaders.length > 0 && (
                  <div
                    style={{
                      overflowX: "auto",
                      maxWidth: "100%",
                      minWidth: `${columnHeaders.length * 100}px`,
                    }}
                  >
                    <div style={{}}>
                      <HighchartsReact
                        highcharts={Highcharts}
                        options={tableHeatmapOptions}
                      />
                    </div>
                  </div>
                )}

              {effectiveChartType === "Stacked Column Chart" &&
                stackedChartOptions && (
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={stackedChartOptions}
                  />
                )}

              {effectiveChartType === "Dot Chart" &&
                rowKeys.length > 0 &&
                colKeys.length > 0 && (
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={dotPlotOptions}
                  />
                )}

              {effectiveChartType === "Map Chart" && (
                <div>
                  <div className="flex justify-end items-center m-2 ">
                    {/* {totalValue && (
                      <div className="font-bold text-gray-600">
                        <span className="text-black">Total:</span>{" "}
                        {totalValue?.toLocaleString()}
                      </div>
                    )} */}
                    <div className="flex justify-end  gap-4 ">
                      <h1 className="font-bold text-gray-400">
                        Change Map Color
                      </h1>
                      <input
                        style={{
                          width: "44px",
                          height: "28px",
                          padding: 0,
                          border: "none",
                          cursor: "pointer",
                          borderRadius: "70%",
                          overflow: "hidden",
                        }}
                        value={mapColor}
                        type="color"
                        onChange={(e) => setMapColor(e.target.value)}
                      />
                    </div>
                  </div>
                  <HighchartsReact
                    constructorType="mapChart"
                    highcharts={Highcharts}
                    options={mapChartOptions}
                  />
                </div>
              )}
            </div>
          </div>
        </DragDropContext>
      )}
    </div>
  );
};

export default CensusPivotTable;
